import { useState } from 'react'
import { Building2 } from 'lucide-react'
import { cn } from '@/lib/utils'

/**
 * A customer's own mark, legible on either theme.
 *
 * ## The problem this exists for
 *
 * Livprom's logo has near-black lettering baked into the image, so on the dark
 * theme half the wordmark vanished — "Liv" in orange and then nothing. Nothing
 * here can fix that by recolouring: it is a customer file at a URL we neither
 * control nor can inspect.
 *
 * Two answers, in order of preference:
 *
 * 1. **A light-on-dark variant, if they have one.** More customers do than it
 *    sounds — Livprom already had one sitting unused in this repository as
 *    `livprom-logo-white.png`. Swapped in with `dark:hidden` / `hidden
 *    dark:block` rather than by reading the theme in JavaScript: the theme is a
 *    class on the root element, so CSS knows the answer on the first paint,
 *    while React would show a frame of the wrong logo on every load.
 *
 * 2. **The mark itself painted white, if they do not.** `brightness-0` crushes
 *    every pixel to black, `invert` then flips it to white — so any logo,
 *    whatever colours it started with, becomes a solid white silhouette that
 *    reads on a dark panel. Transparency is untouched, so it is the mark that
 *    turns white and not a box around it.
 *
 *    This loses the brand colour, which is why it is the fallback and not the
 *    first answer. A plate behind the logo was tried and rejected: it keeps the
 *    colour but puts a white rectangle in the middle of a dark sidebar, which
 *    looks like a rendering fault rather than a design.
 *
 * Both degrade to the fallback icon if the URL is dead, which is the expected
 * case eventually: companies move their assets, and a broken-image icon in
 * somebody's sidebar is a worse look than no branding at all.
 */
export function CompanyLogo({
  src,
  darkSrc,
  name,
  className,
  fallback = 'icon',
}: {
  src: string | null | undefined
  darkSrc?: string | null
  name: string
  /** Sizing for the image itself. Height-capped, never fixed width. */
  className?: string
  /** What to show when there is no usable logo. */
  fallback?: 'icon' | 'none'
}) {
  const [lightFailed, setLightFailed] = useState(false)
  const [darkFailed, setDarkFailed] = useState(false)

  const light = src && !lightFailed ? src : null
  const dark = darkSrc && !darkFailed ? darkSrc : null

  if (!light) {
    if (fallback === 'none') return null

    return (
      <span
        className={cn(
          'flex size-10 shrink-0 items-center justify-center rounded-lg',
          'bg-slate-100 dark:bg-slate-800',
        )}
      >
        <Building2 className="size-5 text-slate-400" aria-hidden />
      </span>
    )
  }

  /*
   * Capped rather than sized: a customer's file is whatever aspect ratio they
   * happen to have, and a header that changes height per company is a layout
   * that breaks for somebody.
   */
  const size = cn('w-auto object-contain', className ?? 'max-h-6 max-w-28')

  return (
    <>
      <span className={cn('shrink-0', dark && 'dark:hidden')}>
        <img
          src={light}
          alt={name}
          className={cn(
            size,
            // No dark variant, so paint the mark itself white — see above.
            !dark && 'dark:brightness-0 dark:invert',
          )}
          onError={() => setLightFailed(true)}
        />
      </span>

      {dark && (
        <span className="hidden shrink-0 dark:block">
          {/* Degrades to the light mark on its plate rather than to a blank
              space where the branding was. */}
          <img src={dark} alt={name} className={size} onError={() => setDarkFailed(true)} />
        </span>
      )}
    </>
  )
}
